import { useState, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { validateCalculatorInputs, calculateVolumeTotal as calcVolTotal, calculateDistributionTotal as calcDistTotal } from '@/components/calculator/ValidationHelpers';

/**
 * Custom hook for pricing calculations and related operations
 * Handles: main calculation, merchant fees, processor changes, service changes
 */
export function usePricingCalculations({
  scenarioInfo,
  setScenarioInfo,
  volumeAssumptions,
  setVolumeAssumptions,
  proposedPricing,
  setProposedPricing,
  transactionDistribution,
  setTransactionDistribution,
  setResult,
  setProfitabilityResult,
  readerTypesWithCosts,
  fetchProposalScenarios,
  selectedProposalId,
  enableIncumbentComparison,
  incumbentPricing
}) {
  const [calculating, setCalculating] = useState(false);
  const [loadingDistribution, setLoadingDistribution] = useState(false);
  const [totalPaymentsAutoCalculated, setTotalPaymentsAutoCalculated] = useState(true);
  const [merchantFeesAutoCalculated, setMerchantFeesAutoCalculated] = useState(true);
  const [originalMerchantFees, setOriginalMerchantFees] = useState({
    ACH: 0,
    CC: 0,
    AMEX: 0,
    DEBIT: 0
  });
  const [achFeeType, setAchFeeType] = useState('flat');
  const [usingStateOverride, setUsingStateOverride] = useState(false);
  const [stateOverrideInfo, setStateOverrideInfo] = useState(null);

  // ═══════════════════════════════════════════════════════════
  // TOTAL PAYMENTS CALCULATION
  // ═══════════════════════════════════════════════════════════

  const calculateTotalPayments = useCallback(() => {
    // Excel Formula: Total Payments ($000s) = SUMPRODUCT(% × Total_Txns, High) / 1000
    const totalTxns = volumeAssumptions.totalTransactions || 0;
    let weightedSum = 0;
    
    transactionDistribution.forEach(item => {
      const high = parseFloat(item.high) || 0;
      const pct = parseFloat(item.currentPercent) / 100 || 0;
      weightedSum += pct * totalTxns * high;
    });
    
    const calculatedPayments = Math.round(weightedSum / 1000); // Convert to $000s
    
    setVolumeAssumptions(prev => ({
      ...prev,
      totalPayments: calculatedPayments
    }));
  }, [volumeAssumptions.totalTransactions, transactionDistribution, setVolumeAssumptions]);

  const handleTotalPaymentsChange = useCallback((value) => {
    setTotalPaymentsAutoCalculated(false);
    setVolumeAssumptions(prev => ({
      ...prev,
      totalPayments: value
    }));
  }, [setVolumeAssumptions]);

  const resetTotalPaymentsToAuto = useCallback(() => {
    setTotalPaymentsAutoCalculated(true);
    calculateTotalPayments();
    toast.success('Total Payments set to auto-calculate');
  }, [calculateTotalPayments]);

  // ═══════════════════════════════════════════════════════════
  // MERCHANT FEES & PROCESSOR MANAGEMENT
  // ═══════════════════════════════════════════════════════════

  const fetchMerchantFeesForService = useCallback(async (serviceName, cardProcessor, achProcessor) => {
    if (!serviceName) return;
    
    try {
      // Get service ID from service name
      const servicesResponse = await api.get('/admin/services');
      const service = servicesResponse.data.services.find(s => s.name === serviceName);
      if (!service) {
        console.warn(`Service ${serviceName} not found`);
        return;
      }
      const serviceId = service.id;
      
      let cardFees = null;
      let achFees = null;
      
      // Fetch all processors
      const processorsResponse = await api.get('/processors');
      const allProcessors = processorsResponse.data.processors;
      
      // Find Card Processor and get its fees
      if (cardProcessor && cardProcessor !== 'N/A') {
        const processor = allProcessors.find(p => p.name === cardProcessor);
        if (processor && processor.service_fees && processor.service_fees[serviceId]) {
          cardFees = processor.service_fees[serviceId];
        }
      }
      
      // Find ACH Processor and get its fees
      if (achProcessor && achProcessor !== 'N/A') {
        const processor = allProcessors.find(p => p.name === achProcessor);
        if (processor && processor.service_fees && processor.service_fees[serviceId]) {
          achFees = processor.service_fees[serviceId];
          
          // Get ACH fee type
          const achFeeTypeFromProcessor = (processor.fee_types && processor.fee_types[serviceId] && processor.fee_types[serviceId].ACH) 
            ? processor.fee_types[serviceId].ACH 
            : 'flat';
          setAchFeeType(achFeeTypeFromProcessor);
          window._achFeeType = achFeeTypeFromProcessor;
        }
      }
      
      // Calculate merchant fee values
      const achFeeTypeForCalc = window._achFeeType || 'flat';
      const newMerchantFees = {
        ACH: achFees && achFees.ACH ? (achFeeTypeForCalc === 'flat' ? achFees.ACH : achFees.ACH * 100) : 0,
        CC: cardFees && cardFees.CC ? (cardFees.CC * 100) : 0,
        AMEX: cardFees && cardFees.AMEX ? (cardFees.AMEX * 100) : (cardFees && cardFees.CC ? (cardFees.CC * 100) : 0),
        DEBIT: cardFees && cardFees.DEBIT ? (cardFees.DEBIT * 100) : (cardFees && cardFees.CC ? (cardFees.CC * 100) : 0)
      };
      
      // Update proposed pricing with merchant fees
      setProposedPricing(prev => ({
        ...prev,
        paymentRates: {
          ACH: { ...prev.paymentRates.ACH, merchantFee: newMerchantFees.ACH },
          CC: { ...prev.paymentRates.CC, merchantFee: newMerchantFees.CC },
          AMEX: { ...prev.paymentRates.AMEX, merchantFee: newMerchantFees.AMEX },
          DEBIT: { ...prev.paymentRates.DEBIT, merchantFee: newMerchantFees.DEBIT }
        }
      }));
      
      setOriginalMerchantFees(newMerchantFees);
      setMerchantFeesAutoCalculated(true);
      
      const processors = [cardProcessor, achProcessor].filter(p => p && p !== 'N/A').join(' & ');
      if (processors) {
        toast.success(`💰 Merchant fees loaded for ${serviceName} × ${processors}`);
      }
    } catch (error) {
      console.error('Error fetching merchant fees:', error);
      toast.warning('Using default merchant fee rates');
    }
  }, [setProposedPricing]);

  const handleProcessorChange = useCallback(async (processorType, processorName) => {
    if (scenarioInfo.service) {
      const cardProc = processorType === 'card' ? processorName : scenarioInfo.cardProcessor;
      const achProc = processorType === 'ach' ? processorName : scenarioInfo.achProcessor;
      await fetchMerchantFeesForService(scenarioInfo.service, cardProc, achProc);
    }
  }, [scenarioInfo.service, scenarioInfo.cardProcessor, scenarioInfo.achProcessor, fetchMerchantFeesForService]);

  // ═══════════════════════════════════════════════════════════
  // SERVICE & DISTRIBUTION MANAGEMENT
  // ═══════════════════════════════════════════════════════════

  const handleServiceChange = useCallback(async (serviceName) => {
    setScenarioInfo({ ...scenarioInfo, service: serviceName });
    
    if (serviceName) {
      setLoadingDistribution(true);
      try {
        // Fetch service data
        const response = await api.get('/admin/services');
        const services = response.data.services || [];
        
        let selectedService = services.find(s => s.name === serviceName);
        if (!selectedService) {
          selectedService = services.find(s => s.name.toLowerCase() === serviceName.toLowerCase());
        }
        
        if (selectedService) {
          // Check for state-service distribution override
          let distributionToUse = selectedService.distribution_profile;
          let isOverride = false;
          let overrideState = null;
          
          if (scenarioInfo.state && selectedService.id) {
            try {
              const overrideResponse = await api.get('/state-service-distributions/lookup', {
                params: {
                  state: scenarioInfo.state,
                  service_id: selectedService.id
                }
              });
              
              if (overrideResponse.data.distribution && overrideResponse.data.distribution.is_active) {
                distributionToUse = overrideResponse.data.distribution.distribution_profile;
                isOverride = true;
                overrideState = scenarioInfo.state;
                setUsingStateOverride(true);
                setStateOverrideInfo({
                  state: scenarioInfo.state,
                  service: serviceName
                });
              } else {
                setUsingStateOverride(false);
                setStateOverrideInfo(null);
              }
            } catch (overrideError) {
              setUsingStateOverride(false);
              setStateOverrideInfo(null);
            }
          } else {
            setUsingStateOverride(false);
            setStateOverrideInfo(null);
          }
          
          if (distributionToUse && distributionToUse.length > 0) {
            // Map distribution_profile to Block 5 format
            const mappedDistribution = distributionToUse.map((item, index) => {
              let collectionAmount;
              if (index === 0 && item.low <= 10) {
                collectionAmount = `≤ $${item.high}`;
              } else if (item.high >= 10000) {
                collectionAmount = `${item.low.toLocaleString()}-${item.high.toLocaleString()}`;
              } else if (item.high >= 1000) {
                collectionAmount = `${item.low.toLocaleString()}-${item.high.toLocaleString()}`;
              } else {
                collectionAmount = `${item.low}-${item.high}`;
              }
              
              return {
                collectionAmount: collectionAmount,
                low: item.low,
                mid: item.mid,
                high: item.high,
                currentPercent: parseFloat((item.pct * 100).toFixed(2))
              };
            });
            
            setTransactionDistribution(mappedDistribution);
            
            if (isOverride) {
              toast.success(`📍 Loaded ${overrideState}-specific distribution for ${serviceName}`, { duration: 4000 });
            } else {
              toast.success(`📊 Loaded ${serviceName} distribution from Services Management`);
            }
          } else {
            toast.warning(`No distribution data found for ${serviceName}`);
          }
        }
        
        // Fetch merchant fees
        await fetchMerchantFeesForService(serviceName, scenarioInfo.cardProcessor, scenarioInfo.achProcessor);
      } catch (error) {
        console.error('[usePricingCalculations] Error loading service distribution:', error);
        toast.error('Failed to load service distribution. Using current distribution.');
        setUsingStateOverride(false);
        setStateOverrideInfo(null);
      } finally {
        setLoadingDistribution(false);
      }
    }
  }, [scenarioInfo, setScenarioInfo, setTransactionDistribution, fetchMerchantFeesForService]);

  const handleStateChange = useCallback(async (stateName, US_STATES) => {
    const selectedState = US_STATES.find(s => s.name === stateName);
    if (selectedState) {
      setScenarioInfo(prev => ({ 
        ...prev, 
        state: selectedState.code,
        selectedStateFull: stateName
      }));
      
      // Re-fetch distribution for state-service combination
      if (scenarioInfo.service) {
        await handleServiceChange(scenarioInfo.service);
      }
    }
  }, [scenarioInfo.service, setScenarioInfo, handleServiceChange]);

  const handleResetToDefaultDistribution = useCallback(async () => {
    if (!scenarioInfo.service) return;
    
    setLoadingDistribution(true);
    try {
      const response = await api.get('/admin/services');
      const services = response.data.services || [];
      
      let selectedService = services.find(s => s.name === scenarioInfo.service);
      if (!selectedService) {
        selectedService = services.find(s => s.name.toLowerCase() === scenarioInfo.service.toLowerCase());
      }
      
      if (selectedService && selectedService.distribution_profile && selectedService.distribution_profile.length > 0) {
        const mappedDistribution = selectedService.distribution_profile.map((item, index) => {
          let collectionAmount;
          if (index === 0 && item.low <= 10) {
            collectionAmount = `≤ $${item.high}`;
          } else if (item.high >= 10000) {
            collectionAmount = `${item.low.toLocaleString()}-${item.high.toLocaleString()}`;
          } else if (item.high >= 1000) {
            collectionAmount = `${item.low.toLocaleString()}-${item.high.toLocaleString()}`;
          } else {
            collectionAmount = `${item.low}-${item.high}`;
          }
          
          return {
            collectionAmount: collectionAmount,
            low: item.low,
            mid: item.mid,
            high: item.high,
            currentPercent: parseFloat((item.pct * 100).toFixed(2))
          };
        });
        
        setTransactionDistribution(mappedDistribution);
        setUsingStateOverride(false);
        setStateOverrideInfo(null);
        toast.success(`📊 Reset to default ${scenarioInfo.service} distribution`);
      }
    } catch (error) {
      console.error('[usePricingCalculations] Error resetting distribution:', error);
      toast.error('Failed to reset distribution');
    } finally {
      setLoadingDistribution(false);
    }
  }, [scenarioInfo.service, setTransactionDistribution]);

  // ═══════════════════════════════════════════════════════════
  // MAIN CALCULATION
  // ═══════════════════════════════════════════════════════════

  const handleCalculate = useCallback(async () => {
    // Validation
    const errors = validateCalculatorInputs(scenarioInfo, volumeAssumptions, proposedPricing, transactionDistribution);
    
    if (errors.length > 0) {
      const errorMessage = errors.length === 1 
        ? errors[0] 
        : `Please fill in all required fields:\n• ${errors.slice(0, 5).join('\n• ')}${errors.length > 5 ? `\n• ... and ${errors.length - 5} more` : ''}`;
      toast.error(errorMessage, { duration: 5000 });
      return;
    }
    
    // Validate percentages
    const volumeTotal = parseFloat(calcVolTotal(volumeAssumptions));
    const distributionTotal = parseFloat(calcDistTotal(transactionDistribution));

    if (Math.abs(volumeTotal - 100) > 0.01) {
      toast.error('Volume assumptions must sum to 100%');
      return;
    }

    if (Math.abs(distributionTotal - 100) > 0.01) {
      toast.error('Transaction distribution must sum to 100%');
      return;
    }

    try {
      setCalculating(true);
      toast.info('Calculating pricing...');

      const requestData = {
        scenario_info: {
          ...scenarioInfo,
          municipality: scenarioInfo.municipality,
          accountExecutive: scenarioInfo.accountExecutive,
          state: scenarioInfo.state
        },
        volume_assumptions: {
          totalPayments: volumeAssumptions.totalPayments,
          totalTransactions: volumeAssumptions.totalTransactions,
          achPercent: volumeAssumptions.achPercent,
          ccPercent: volumeAssumptions.ccPercent,
          amexPercent: volumeAssumptions.amexPercent,
          debitPercent: volumeAssumptions.debitPercent
        },
        incumbent_pricing: {
          tier1MaxAmount: proposedPricing.tier1MaxAmount,
          tier1MinFee: proposedPricing.tier1MinFee,
          tier2MaxAmount: proposedPricing.tier2MaxAmount,
          tier2MinFee: proposedPricing.tier2MinFee,
          tier3MaxAmount: proposedPricing.tier3MaxAmount,
          tier3MinFee: proposedPricing.tier3MinFee,
          paymentRates: proposedPricing.paymentRates
        },
        proposed_pricing: {
          tier1MaxAmount: proposedPricing.tier1MaxAmount,
          tier1MinFee: proposedPricing.tier1MinFee,
          tier2MaxAmount: proposedPricing.tier2MaxAmount,
          tier2MinFee: proposedPricing.tier2MinFee,
          tier3MaxAmount: proposedPricing.tier3MaxAmount,
          tier3MinFee: proposedPricing.tier3MinFee,
          paymentRates: proposedPricing.paymentRates,
          achFlatFeeAddOn: proposedPricing.achFlatFeeAddOn
        },
        transaction_distribution: transactionDistribution,
        ach_fee_type: achFeeType
      };

      const response = await api.post('/pricing/calculate', requestData);
      setResult(response.data);
      
      // Get reader costs
      const selectedReaderType = readerTypesWithCosts.find(rt => rt.name === scenarioInfo.readerType);
      const readerCostPerUnit = selectedReaderType
        ? (selectedReaderType.monthly_cost || selectedReaderType.cost_per_unit || 0)
        : 0;
      const hasSim = selectedReaderType ? selectedReaderType.has_sim : false;
      const simCost = selectedReaderType ? selectedReaderType.sim_cost : 0;
      
      // Build profitability request with correct structure for backend
      const profitabilityRequest = {
        client_info: {
          ...scenarioInfo,
          billingFrequency: scenarioInfo.billingFrequency || 'Annual',
          reader_cost_per_unit: readerCostPerUnit,
          has_sim: hasSim,
          sim_cost: simCost
        },
        volume_assumptions: volumeAssumptions,
        proposed_pricing: proposedPricing,
        transaction_distribution: transactionDistribution,
        ach_fee_type: achFeeType,
        reader_costs: {
          quantity: scenarioInfo.readersQty || 0,
          cost_per_reader: readerCostPerUnit,
          has_sim: hasSim,
          sim_cost: simCost
        },
        // Include incumbent pricing for comparison if enabled
        incumbent_pricing: enableIncumbentComparison ? incumbentPricing : null
      };

      // Debug logging
      console.log('[Calculate] Profitability Request:', JSON.stringify(profitabilityRequest, null, 2));
      console.log('[Calculate] Volume Assumptions:', volumeAssumptions);
      console.log('[Calculate] Total Transactions:', volumeAssumptions.totalTransactions);
      console.log('[Calculate] Total Payments:', volumeAssumptions.totalPayments);
      console.log('[Calculate] Proposed Pricing:', proposedPricing);

      const profitabilityResponse = await api.post('/pricing/calculate-gw-profitability', profitabilityRequest);
      
      console.log('[Calculate] Profitability Response:', JSON.stringify(profitabilityResponse.data, null, 2));
      
      setProfitabilityResult(profitabilityResponse.data);
      toast.success('✅ Calculation complete!');
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error(error.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  }, [
    scenarioInfo,
    volumeAssumptions,
    proposedPricing,
    transactionDistribution,
    achFeeType,
    readerTypesWithCosts,
    setResult,
    setProfitabilityResult
  ]);

  // Helper calculation functions
  const calculateVolumeTotal = () => calcVolTotal(volumeAssumptions);
  const calculateDistributionTotal = () => calcDistTotal(transactionDistribution);

  return {
    // State
    calculating,
    loadingDistribution,
    totalPaymentsAutoCalculated,
    merchantFeesAutoCalculated,
    originalMerchantFees,
    achFeeType,
    usingStateOverride,
    stateOverrideInfo,
    // State setters
    setMerchantFeesAutoCalculated,
    setUsingStateOverride,
    setStateOverrideInfo,
    // Handlers
    calculateTotalPayments,
    handleTotalPaymentsChange,
    resetTotalPaymentsToAuto,
    fetchMerchantFeesForService,
    handleProcessorChange,
    handleServiceChange,
    handleStateChange,
    handleResetToDefaultDistribution,
    handleCalculate,
    calculateVolumeTotal,
    calculateDistributionTotal
  };
}
